import type { CaseFileData } from '@/api';
import { FileStatus } from '@/types/file';

/**
 * Temporary mock audio rows for testing the audio player before the BE serves recordings. Shown via
 * the "Show mock audio" toggle. Remove once the audio BE is ready.
 */
export const MOCK_AUDIO_CASE_FILE_ID = 'mock-audio-recording';
export const MOCK_TRANSCRIBED_AUDIO_CASE_FILE_ID = 'mock-audio-recording-transcribed';
export const MOCK_AUDIO_TRANSCRIPT_CASE_FILE_ID = 'mock-audio-recording-transcript';
export const MOCK_FAILING_AUDIO_CASE_FILE_ID = 'mock-audio-recording-failing';

/** Used to tell the injected rows apart from real ones coming back from the API. */
const MOCK_AUDIO_CASE_FILE_IDS = new Set<string | null>([
  MOCK_AUDIO_CASE_FILE_ID,
  MOCK_TRANSCRIBED_AUDIO_CASE_FILE_ID,
  MOCK_AUDIO_TRANSCRIPT_CASE_FILE_ID,
  MOCK_FAILING_AUDIO_CASE_FILE_ID,
]);

/** Real, locally-served audio file so playback controls can be tested end-to-end. */
export const MOCK_AUDIO_URL = '/mock/client_call_recording.mp3';

export const mockAudioCaseFile: CaseFileData = {
  documentId: MOCK_AUDIO_CASE_FILE_ID,
  historyRowId: MOCK_AUDIO_CASE_FILE_ID,
  documentName: 'client_call_recording.mp3',
  details: 'Client call recording',
  uploadedBy: 'LBS',
  uploadedAt: '2025-10-18T09:00:00.000Z',
  sizeMb: 4.2,
  numberOfPages: 0,
  status: 'Untranscribed',
  source: 'PROCLAIM',
  historyRowColumns: {
    Handler: 'LBS',
    'Corres. to': 'Mr L Khan',
    'Proclaim Folder': '',
  },
  categories: {
    Category: 'Call Recording',
    'Sub-Category': 'Audio',
  },
  documentFormat: 'mp3',
  hasDocument: true,
  isDisplayable: true,
  warningDetails: '',
  warnings: [],
};

/** Temporary mock transcript, nested under its recording via `attachments`. */
export const mockAudioTranscriptCaseFile: CaseFileData = {
  documentId: MOCK_AUDIO_TRANSCRIPT_CASE_FILE_ID,
  historyRowId: MOCK_AUDIO_TRANSCRIPT_CASE_FILE_ID,
  documentName: 'Transcript.docx',
  details: '',
  uploadedBy: 'LBS',
  uploadedAt: '2025-10-18T09:00:00.000Z',
  sizeMb: 0.1,
  numberOfPages: 12,
  status: FileStatus.READY_FOR_CHAT,
  source: 'FLETCH',
  historyRowColumns: {
    Handler: 'LBS',
    'Corres. to': 'Mr L Khan',
    'Proclaim Folder': '',
  },
  categories: {
    Category: 'Call Recording',
    'Sub-Category': 'Transcript',
  },
  documentFormat: 'docx',
  hasDocument: true,
  isDisplayable: true,
  warningDetails: '',
  warnings: [],
};

/** Same recording, already transcribed, with the transcript as a collapsed child row. */
export const mockTranscribedAudioCaseFile: CaseFileData = {
  ...mockAudioCaseFile,
  documentId: MOCK_TRANSCRIBED_AUDIO_CASE_FILE_ID,
  historyRowId: MOCK_TRANSCRIBED_AUDIO_CASE_FILE_ID,
  details: 'Client call recording (transcribed)',
  status: 'Ready',
  attachments: [mockAudioTranscriptCaseFile],
};

/** Same recording again, whose transcription always fails, so the failure toast can be tested. */
export const mockFailingAudioCaseFile: CaseFileData = {
  ...mockAudioCaseFile,
  documentId: MOCK_FAILING_AUDIO_CASE_FILE_ID,
  historyRowId: MOCK_FAILING_AUDIO_CASE_FILE_ID,
  details: 'Client call recording (fails)',
};

/** Off by default; remembered per browser. */
const MOCK_AUDIO_ENABLED_STORAGE_KEY = 'fletch:mock-audio-files';

export const readMockAudioFilesEnabled = (): boolean => {
  try {
    return localStorage.getItem(MOCK_AUDIO_ENABLED_STORAGE_KEY) === '1';
  } catch {
    // Access can fail (privacy mode) — the rows just stay hidden.
    return false;
  }
};

export const persistMockAudioFilesEnabled = (enabled: boolean): void => {
  try {
    if (enabled) {
      localStorage.setItem(MOCK_AUDIO_ENABLED_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(MOCK_AUDIO_ENABLED_STORAGE_KEY);
    }
  } catch {
    // Not remembering the choice only costs a click after a reload.
  }
};

export const isMockAudioCaseFileId = (id: string | null | undefined): boolean =>
  MOCK_AUDIO_CASE_FILE_IDS.has(id ?? null);

/** Adds the mock rows when enabled and removes previously added ones. */
export const withMockAudioFiles = (files: CaseFileData[], enabled: boolean): CaseFileData[] => {
  const realFiles = files.filter((file) => !MOCK_AUDIO_CASE_FILE_IDS.has(file.documentId));

  return enabled
    ? [mockAudioCaseFile, mockTranscribedAudioCaseFile, mockFailingAudioCaseFile, ...realFiles]
    : realFiles;
};
